import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send, MessageSquare, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Section } from "@/components/Section";

const PURPOSE_OPTIONS = [
  "Equipment Requirement",
  "Service Requirement",
  "Spare Requirement",
  "Finance / EMI Consultation",
  "Rental / Hire Enquiry",
  "Other",
];

const CONSULTATION_INITIAL = { name: "", email: "", phone: "", subject: "Equipment Requirement", message: "" };
const ASSOCIATE_INITIAL = { name: "", email: "", phone: "", city: "", current_profession: "", why_associate: "" };

function Input({ label, ...rest }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</label>
      <input className="mt-2 w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00]" {...rest} />
    </div>
  );
}

export default function Contact() {
  const { settings } = useSettings();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("type") === "associate" ? "associate" : "consultation";
  const [tab, setTab] = useState(initialTab);
  const [consForm, setConsForm] = useState(CONSULTATION_INITIAL);
  const [assocForm, setAssocForm] = useState(ASSOCIATE_INITIAL);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const submitConsultation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads/contact", { ...consForm, lead_type: "consultation" });
      toast.success(data.message || "Message sent!");
      setConsForm(CONSULTATION_INITIAL);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to submit.");
    } finally { setSubmitting(false); }
  };

  const submitAssociate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: assocForm.name,
        email: assocForm.email,
        phone: assocForm.phone,
        subject: "Become An Associate",
        message: `City: ${assocForm.city}\nCurrent Profession: ${assocForm.current_profession}\nWhy Associate: ${assocForm.why_associate}`,
        lead_type: "associate",
      };
      const { data } = await api.post("/leads/contact", payload);
      toast.success(data.message || "Application received!");
      setAssocForm(ASSOCIATE_INITIAL);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <section className="bg-[#1A1A1A] text-white py-20 noise-overlay">
        <Section>
          <div className="label-eyebrow mb-4">Contact</div>
          <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">
            LET'S <span className="text-[#E65C00]">TALK</span> EQUIPMENT.
          </h1>
          <p className="mt-4 text-gray-400 max-w-xl">Tell us what you need. A Tara Autohub expert will reach out within 2 working hours.</p>
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          {/* Tabs — attached, no white gap */}
          <div className="flex border-b-2 border-gray-200 mb-6" data-testid="contact-tabs">
            <button
              onClick={() => setTab("consultation")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 font-heading font-black uppercase tracking-widest text-sm -mb-0.5 border-b-4 transition-colors ${
                tab === "consultation" ? "border-[#E65C00] text-[#1A1A1A] bg-[#FFCC00]/10" : "border-transparent text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-50"
              }`}
              data-testid="contact-tab-consultation"
            >
              <MessageSquare className="w-4 h-4" /> Get Consultation
            </button>
            <button
              onClick={() => setTab("associate")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 font-heading font-black uppercase tracking-widest text-sm -mb-0.5 border-b-4 transition-colors border-l border-gray-200 ${
                tab === "associate" ? "border-b-[#E65C00] text-[#1A1A1A] bg-[#FFCC00]/10" : "border-b-transparent text-gray-500 hover:text-[#1A1A1A] hover:bg-gray-50"
              }`}
              data-testid="contact-tab-associate"
            >
              <Handshake className="w-4 h-4" /> Become An Associate
            </button>
          </div>

          {tab === "consultation" ? (
            <form onSubmit={submitConsultation} className="space-y-5" data-testid="contact-form-consultation">
              <p className="text-gray-600 text-sm">Get a free equipment consultation, finance quote or site visit. Our experts respond within 2 working hours.</p>
              <div className="grid md:grid-cols-2 gap-5">
                <Input label="Your Name" required value={consForm.name} onChange={(e) => setConsForm({ ...consForm, name: e.target.value })} data-testid="cons-name" />
                <Input label="Email" required type="email" value={consForm.email} onChange={(e) => setConsForm({ ...consForm, email: e.target.value })} data-testid="cons-email" />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Input label="Phone" value={consForm.phone} onChange={(e) => setConsForm({ ...consForm, phone: e.target.value })} data-testid="cons-phone" />
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Purpose</label>
                  <select
                    className="mt-2 w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] bg-white"
                    value={consForm.subject}
                    onChange={(e) => setConsForm({ ...consForm, subject: e.target.value })}
                    data-testid="cons-purpose"
                  >
                    {PURPOSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Tell us what you need</label>
                <textarea required rows={6} className="mt-2 w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] resize-none" value={consForm.message} onChange={(e) => setConsForm({ ...consForm, message: e.target.value })} data-testid="cons-message" />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="cons-submit">
                {submitting ? "Sending..." : <>Send Consultation Request <Send className="w-4 h-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={submitAssociate} className="space-y-5" data-testid="contact-form-associate">
              <div className="bg-[#FFCC00]/20 border-l-4 border-[#E65C00] p-4 text-sm">
                <strong>Earn while you do your current job.</strong> Become a Tara Autohub Associate and earn referral commissions on every successful equipment deal you bring our way. No sales target. No exclusivity.
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Input label="Full Name" required value={assocForm.name} onChange={(e) => setAssocForm({ ...assocForm, name: e.target.value })} data-testid="assoc-name" />
                <Input label="Email" required type="email" value={assocForm.email} onChange={(e) => setAssocForm({ ...assocForm, email: e.target.value })} data-testid="assoc-email" />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Input label="Mobile" required value={assocForm.phone} onChange={(e) => setAssocForm({ ...assocForm, phone: e.target.value })} data-testid="assoc-phone" />
                <Input label="City" required value={assocForm.city} onChange={(e) => setAssocForm({ ...assocForm, city: e.target.value })} data-testid="assoc-city" />
              </div>
              <Input label="Current Profession" value={assocForm.current_profession} onChange={(e) => setAssocForm({ ...assocForm, current_profession: e.target.value })} data-testid="assoc-profession" placeholder="e.g. Civil Contractor, Site Engineer, Equipment Operator" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Why do you want to associate with us?</label>
                <textarea required rows={5} className="mt-2 w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] resize-none" value={assocForm.why_associate} onChange={(e) => setAssocForm({ ...assocForm, why_associate: e.target.value })} data-testid="assoc-why" placeholder="Tell us about your network, region of operation, and how you can refer contractors..." />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="assoc-submit">
                {submitting ? "Submitting..." : <>Apply To Associate <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>

        <aside className="bg-[#1A1A1A] text-white p-7 space-y-6 h-fit lg:sticky lg:top-28" data-testid="contact-info">
          <div>
            <div className="font-heading font-black text-xl uppercase">Reach Us</div>
            <div className="w-12 h-1 bg-[#E65C00] mt-2" />
          </div>
          <div className="flex gap-3 text-sm">
            <Mail className="w-5 h-5 text-[#E65C00] shrink-0 mt-0.5" />
            <a href={`mailto:${settings?.email || ""}`} className="hover:text-[#FFCC00]">{settings?.email}</a>
          </div>
          <div className="flex gap-3 text-sm">
            <Phone className="w-5 h-5 text-[#E65C00] shrink-0 mt-0.5" />
            <a href={`tel:${settings?.phone || ""}`} className="hover:text-[#FFCC00]">{settings?.phone}</a>
          </div>
          <div className="flex gap-3 text-sm">
            <MapPin className="w-5 h-5 text-[#E65C00] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-[#FFCC00] text-xs uppercase tracking-widest">Reg. Office</div>
              <div className="text-gray-300 mt-1">{settings?.address}</div>
              <div className="font-bold text-[#FFCC00] text-xs uppercase tracking-widest mt-4">Head Office</div>
              <div className="text-gray-300 mt-1">{settings?.head_office}</div>
              <div className="font-bold text-[#FFCC00] text-xs uppercase tracking-widest mt-4">Zonal Office</div>
              <div className="text-gray-300 mt-1">{settings?.zonal_office}</div>
            </div>
          </div>
        </aside>
      </Section>

      {settings?.map_embed_url && (
        <Section className="pb-20">
          <div className="border-4 border-[#1A1A1A]">
            <iframe
              title="Tara Autohub Location"
              src={settings.map_embed_url}
              width="100%" height="420"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </Section>
      )}
    </>
  );
}
